/**
 * Shared formatters for the coaching vertical slice. All formatting is
 * timezone-aware relative to the athlete timezone reported by Stage 9;
 * we never assume the browser timezone matches the athlete's.
 */
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const safeTz = (tz) => (tz && typeof tz === "string" ? tz : "UTC");

// Strict date-only shape emitted by the backend for calendar dates
// (Pydantic ``date`` fields serialise as ``YYYY-MM-DD`` under
// ``model_dump(mode="json")``). These values are TIMEZONE-NEUTRAL —
// ``2026-09-15`` means the calendar date, not an instant. Converting
// them through ``dayjs(value).tz(...)`` silently shifts them when the
// browser timezone differs from the target timezone (in India, for
// example, ``dayjs("2026-09-15")`` parses to local midnight IST
// ``2026-09-14T18:30:00Z`` and formatting in UTC then renders
// "Mon Sep 14"). We must NEVER let a strict date-only value shift due
// to timezone (see PERMANENT TRAINING DAY / CALENDAR DATE AUTHORITY
// FIX). True timestamps continue to be timezone-aware.
const STRICT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isStrictDate = (value) =>
  typeof value === "string" && STRICT_DATE_RE.test(value);

const parse = (value) => {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

// Parse a strict ``YYYY-MM-DD`` into a timezone-neutral dayjs instance
// (fixed at UTC so subsequent ``.format`` cannot accidentally shift the
// calendar date). Callers formatting strict dates must NEVER call
// ``.tz(...)`` on this instance.
const parseStrictDate = (value) => {
  if (!isStrictDate(value)) return null;
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed : null;
};

export const formatDate = (value, tz) => {
  if (isStrictDate(value)) {
    const d = parseStrictDate(value);
    return d ? d.format("ddd, MMM D, YYYY") : "—";
  }
  const parsed = parse(value);
  if (!parsed) return "—";
  try {
    return parsed.tz(safeTz(tz)).format("ddd, MMM D, YYYY");
  } catch {
    return parsed.format("ddd, MMM D, YYYY");
  }
};

export const formatDateTime = (value, tz) => {
  // A strict date-only value has no time component; render it as a date.
  if (isStrictDate(value)) {
    const d = parseStrictDate(value);
    return d ? d.format("ddd, MMM D · 00:00") : "—";
  }
  const parsed = parse(value);
  if (!parsed) return "—";
  try {
    return parsed.tz(safeTz(tz)).format("ddd, MMM D · HH:mm");
  } catch {
    return parsed.format("ddd, MMM D · HH:mm");
  }
};

export const formatTime = (value, tz) => {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.slice(0, 5);
  }
  // Strict date-only values have no time; return em-dash rather than
  // silently formatting midnight in some timezone.
  if (isStrictDate(value)) return "—";
  const parsed = parse(value);
  if (!parsed) return String(value);
  try {
    return parsed.tz(safeTz(tz)).format("HH:mm");
  } catch {
    return parsed.format("HH:mm");
  }
};

export const formatDuration = (minutes) => {
  if (minutes == null || !Number.isFinite(Number(minutes))) return "—";
  const m = Number(minutes);
  if (m < 60) return `${Math.round(m)} min`;
  const hours = Math.floor(m / 60);
  const mins = Math.round(m % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export const formatPercent = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const v = Number(value);
  const scaled = v > 1.5 ? v : v * 100;
  return `${Math.round(scaled)}%`;
};

const CONFIDENCE_LABELS = {
  HIGH: "High confidence",
  MODERATE: "Moderate confidence",
  LOW: "Low confidence",
  INSUFFICIENT: "Insufficient data",
};

export const formatConfidence = (value) =>
  CONFIDENCE_LABELS[value] || "—";

const SEVERITY_LABELS = {
  INFO: "Info",
  NOTICE: "Notice",
  WARNING: "Warning",
  HIGH: "Important",
  CRITICAL: "Critical",
};

export const formatSeverity = (value) => SEVERITY_LABELS[value] || value || "Info";

const SPORT_LABELS = {
  cycling: "Cycling",
  running: "Running",
  swimming: "Swimming",
  strength: "Strength",
  triathlon: "Triathlon",
  walking: "Walking",
  rowing: "Rowing",
};

export const formatSport = (sport) => {
  if (!sport) return "—";
  const k = String(sport).toLowerCase();
  return SPORT_LABELS[k] || sport;
};

const DAY_STATUS_LABELS = {
  TRAINING_DAY: "Training day",
  REST_DAY: "Rest day",
  RECOVERY_DAY: "Recovery day",
  EVENT_DAY: "Event day",
  UNSCHEDULED_DAY: "Unscheduled",
  PLAN_SUSPENDED: "Plan suspended",
  CONTEXT_INCOMPLETE: "Loading context…",
};

export const formatDayStatus = (status) =>
  DAY_STATUS_LABELS[status] || status || "—";

const WEEK_TYPE_LABELS = {
  BUILD: "Build week",
  LOADING: "Loading week",
  CONSOLIDATION: "Consolidation week",
  RECOVERY: "Recovery week",
  TAPER: "Taper week",
  EVENT: "Event week",
  PEAK: "Peak week",
};

export const formatWeekType = (type) =>
  WEEK_TYPE_LABELS[type] || type || "—";

const ACTION_LABELS = {
  KEEP_AS_PLANNED: "Keep as planned",
  REMOVE_OPTIONAL_SESSION: "Optional session removed",
  DROP_MISSED_SESSION: "Missed session dropped",
  MOVE_SESSION: "Session moved",
  REPEAT_KEY_SESSION: "Key session repeated",
  SHORTEN_DURATION_VARIANT: "Duration reduced",
  USE_LOWER_COST_SUBSTITUTION: "Lower-cost substitution",
  REPLACE_WITH_RECOVERY_SESSION: "Recovery substituted",
  INSERT_RECOVERY_SESSION: "Recovery inserted",
  INSERT_REST_DAY: "Rest day inserted",
  REDUCE_WEEKLY_VOLUME: "Weekly volume reduced",
  REDUCE_HARD_SESSION_COUNT: "Hard sessions reduced",
  HOLD_PROGRESSION: "Progression held",
  REGRESS_PROGRESSION_LEVEL: "Progression regressed",
  CONVERT_TO_CONSOLIDATION_WEEK: "Week converted to consolidation",
  CONVERT_TO_RECOVERY_WEEK: "Week converted to recovery",
  REGENERATE_REMAINING_WEEK: "Remaining week regenerated",
  REGENERATE_REMAINING_BLOCK: "Remaining block regenerated",
  SUSPEND_PLAN_FOR_SAFETY: "Plan paused for safety",
  REQUEST_CHAT2_REASSESSMENT: "Reassessment required",
};

export const formatAdaptationAction = (code) =>
  ACTION_LABELS[code] || code || "—";

const COMPLIANCE_LABELS = {
  FULL: "Completed",
  MINOR_VARIATION: "Minor variation",
  PARTIAL: "Partial",
  SKIPPED: "Skipped",
  OVERCOMPLETED: "Over-completed",
  AUTHORISED_SUBSTITUTION: "Authorised substitution",
  WITHIN: "Within target",
  BELOW: "Below target",
  ABOVE: "Above target",
  WITHIN_TOLERANCE: "Within tolerance",
  BELOW_TARGET: "Below target",
  ABOVE_TARGET: "Above target",
  ADEQUATE: "Adequate",
  COMPLIANT: "Compliant",
  NON_COMPLIANT: "Non-compliant",
  STABLE: "Stable",
  IMPROVING: "Improving",
  DECLINING: "Declining",
};

export const formatComplianceCategory = (value) =>
  COMPLIANCE_LABELS[value] || value || "—";

const OBJECTIVE_LABELS = {
  CYCLING_THRESHOLD_POWER: "cycling threshold power",
  CYCLING_ENDURANCE: "cycling endurance",
  CYCLING_VO2MAX: "cycling VO2max",
  CYCLING_ANAEROBIC_CAPACITY: "cycling anaerobic capacity",
  CYCLING_NEUROMUSCULAR_POWER: "cycling sprint power",
  CYCLING_MUSCULAR_ENDURANCE: "cycling muscular endurance",
  RUNNING_THRESHOLD: "running threshold",
  RUNNING_VO2MAX: "running VO2max",
  RUNNING_ENDURANCE: "running endurance",
  RUNNING_SPEED: "running speed",
  SWIMMING_CSS: "swimming CSS",
  SWIMMING_ENDURANCE: "swimming endurance",
  SWIMMING_TECHNIQUE: "swim technique",
  STRENGTH_MAX: "maximum strength",
  STRENGTH_ENDURANCE: "strength endurance",
  GENERAL_ENDURANCE: "general endurance",
  GENERAL_RECOVERY: "recovery",
};

export const formatObjective = (value) => {
  if (!value) return "—";
  const key = String(value).toUpperCase();
  if (OBJECTIVE_LABELS[key]) return OBJECTIVE_LABELS[key];
  return String(value).toLowerCase().replaceAll("_", " ");
};

export const formatLimiter = (value) => {
  if (value == null) return "—";
  if (typeof value === "object") {
    const label = value.label || value.name || value.capability_code
                       || value.limiter;
    return label ? formatObjective(label) : "—";
  }
  return formatObjective(value);
};

const ROLE_LABELS = {
  PRIMARY_KEY: "Key session",
  KEY: "Key session",
  LONG: "Long session",
  RECOVERY: "Recovery session",
  OPTIONAL: "Optional session",
  MAINTENANCE: "Maintenance session",
  BRICK: "Brick session",
  STRENGTH: "Strength session",
  SUPPORT: "Support session",
  ENDURANCE: "Endurance session",
  EVENT: "Event",
};

export const formatSessionRole = (value) => {
  if (!value) return "Session";
  const key = String(value).toUpperCase();
  if (ROLE_LABELS[key]) return ROLE_LABELS[key];
  return String(value).toLowerCase().replaceAll("_", " ");
};

const PRESCRIPTION_LABELS = {
  POWER: "Power-based",
  HR: "Heart-rate-based",
  HEART_RATE: "Heart-rate-based",
  PACE: "Pace-based",
  CSS: "CSS-based",
  RPE: "RPE-based",
  RIR: "RIR-based",
  DURATION: "Duration-based",
  ZONE: "Zone-based",
};

export const formatPrescriptionMethod = (value) => {
  if (!value) return "—";
  const key = String(value).toUpperCase();
  return PRESCRIPTION_LABELS[key]
    || String(value).toLowerCase().replaceAll("_", " ");
};

const ADAPTATION_STATUS_LABELS = {
  NO_CHANGE_REQUIRED: "No change required",
  ADAPTATION_RECOMMENDED: "Change recommended",
  ADAPTATION_APPLIED: "Change applied",
  ADAPTATION_PENDING_CONFIRMATION: "Awaiting your confirmation",
  ADAPTATION_APPLICATION_FAILED: "Change could not be applied",
  PLAN_SUSPENDED_FOR_SAFETY: "Training suspended for safety",
  CHAT2_REASSESSMENT_REQUIRED: "Reassessment required",
};

export const formatAdaptationStatus = (value) => {
  if (!value) return "—";
  const key = String(value).toUpperCase();
  return ADAPTATION_STATUS_LABELS[key]
    || String(value).toLowerCase().replaceAll("_", " ");
};

const ADAPTATION_SCOPE_LABELS = {
  SINGLE_SESSION: "One session",
  MULTIPLE_SESSIONS: "Several sessions",
  WEEK_REMAINDER: "Rest of this week",
  BLOCK_REMAINDER: "Rest of this block",
  FULL_BLOCK: "Whole training block",
  ENTIRE_PLAN: "Whole plan",
};

export const formatAdaptationScope = (value) => {
  if (!value) return "—";
  const key = String(value).toUpperCase();
  return ADAPTATION_SCOPE_LABELS[key]
    || String(value).toLowerCase().replaceAll("_", " ");
};

const RPE_LABELS = {
  AS_EXPECTED: "In line with target",
  LOWER_THAN_EXPECTED: "Easier than expected",
  HIGHER_THAN_EXPECTED: "Harder than expected",
  HIGH: "Harder than expected",
  MISSING: "Not recorded",
};

export const formatRpeAgreement = (value) => {
  if (!value) return "—";
  const key = String(value).toUpperCase();
  return RPE_LABELS[key] || String(value).toLowerCase().replaceAll("_", " ");
};

export const formatMinutes = (minutes) => {
  if (minutes == null || !Number.isFinite(Number(minutes))) return "—";
  return `${Math.round(Number(minutes))} min`;
};

/**
 * Deterministic severity → semantic UI token mapping. Used by cards to
 * pick borders/icons/backgrounds; never encodes safety in colour alone
 * — every caller also renders text-based severity labels.
 */
export const severityToken = (severity) => {
  const s = String(severity || "").toUpperCase();
  if (s === "CRITICAL") return "critical";
  if (s === "HIGH") return "high";
  if (s === "WARNING") return "warning";
  if (s === "NOTICE") return "notice";
  return "info";
};

export const _internal = { parse };
